import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Contact.css';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { CiCircleRemove, CiCircleCheck } from "react-icons/ci";
import ContactImg from '../../../src/assets/contact.png';

// Hardcoded reCAPTCHA site key
const RECAPTCHA_SITE_KEY = '6LeX2-opAAAAAHr45FWkOsMM5kF1iYZuz7VrUq6o';

function ContactPage() {
  const [phone, setPhone] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('');
  const [showPopup, setShowPopup] = useState(false);

  const generateRecaptchaToken = async () => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
            .then((token) => {
              resolve(token);
            })
            .catch((error) => {
              console.error("reCAPTCHA execution error (Contact):", error);
              reject(error);
            });
        });
      } else {
        console.error("grecaptcha not defined (Contact)");
        reject(new Error('grecaptcha not defined'));
      }
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const formData = new FormData(event.target);
    formData.append('phone', phone);

    const name = formData.get('name').trim();
    const company = formData.get('company').trim();
    const email = formData.get('email').trim();
    const subject = formData.get('subject').trim();
    const message = formData.get('message').trim();

    if (!name || !company || !email || !subject || !message || !phone) {
      setPopupMessage('Please fill out all required fields.');
      setPopupType('error');
      setShowPopup(true);
      return;
    }

    try {
      const token = await generateRecaptchaToken();
      formData.append('g_recaptcha_response', token);

      const response = await fetch('https://pharm-rx-backend-50d4ad87e02c.herokuapp.com/api/send-message/', {
        method: 'POST',
        body: new URLSearchParams(formData),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        event.target.reset();
        setPhone('');
        setPopupMessage('Your message has been sent successfully');
        setPopupType('success');
        setShowPopup(true);
      } else {
        setPopupMessage('A problem occurred when sending your message');
        setPopupType('error');
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setPopupMessage('A problem occurred when sending your message');
      setPopupType('error');
      setShowPopup(true);
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (!window.grecaptcha) {
        console.error('grecaptcha not defined (Contact)');
      }
    };

    script.onerror = () => {
      console.error('Error loading reCAPTCHA script (Contact)');
    };

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  return (
    <div className='main-container'>
      <Helmet>
        <title>Inquiries and Support: Contact Pharm-Rx</title>
        <meta name="description" content="Get in touch with Pharm-Rx to discuss your pharmaceutical and nutritional ingredient needs. Our experts are just a message away." />
        <meta property="og:title" content="Inquiries and Support: Contact Pharm-Rx" />
        <meta property="og:description" content="Get in touch with Pharm-Rx to discuss your pharmaceutical and nutritional ingredient needs. Our experts are just a message away." />
        <meta property="og:image" content="URL_of_your_social_media_image" />
        <meta property="og:url" content="https://www.pharm-rxchem.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "Organization",
            "name": "Pharm-Rx",
            "url": "https://www.pharm-rxchem.com",
            "description": "Get in touch with Pharm-Rx to discuss your pharmaceutical and nutritional ingredient needs. Our experts are just a message away.",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-973-917-1400",
              "contactType": "Customer Service",
              "availableLanguage": ["English"]
            }
          })}
        </script>
      </Helmet>

      <div className="contact-heading-container">
        <h2 className="contact-heading">Have a question?</h2>
        <p className="contact-subheading">Contact Pharm-Rx, your partner for quality ingredients and reliable service.</p>
      </div>

      <div className="contact-image-section">
        <img 
          className="contact-image"
          src={ContactImg}
          alt="Contact Pharm-Rx"
        />
      </div>

      <div className='contact-font-wrapper'>
        <div className='contact-container'>
          <div className='columns-container'>
            <div className='contact-info'>
              <h2>Points of Contact</h2>
              <p><span className='thick-text'>Pharm-Rx Chemical Corporation</span><br />
                <span className='thin-text'>299 Market Street, Suite 410<br />
                  Saddle Brook, NJ 07663<br />
                </span></p>
              <p><span className='thick-text'>Phone:</span><br /><span className='thin-text'><a href="tel:973-917-1400">(973) 917-1400</a></span><br /></p>
              <p><span className='thick-text'>Fax:</span><br /><span className='thin-text'>(973) 917-1440</span></p>
              <p><span className='thick-text'>General Information:<br />
                <a href="mailto:info@pharm-rxchem.com">info@pharm-rxchem.com</a>
              </span></p>
              <p><span className='thick-text'>Sales & Business Development Inquiries:<br />
                <a href='mailto:mtb@pharm-rxchem.com'>mtb@pharm-rxchem.com</a>
              </span></p>
              <p><span className='thick-text'>Mark Bostel - President & Head of Business Development:<br />
                <a href='mailto:mtb@pharm-rxchem.com'>mtb@pharm-rxchem.com</a>
              </span></p>
              <p><span className='thick-text'>Carlos Doussinague - Executive Vice President:<br />
                <a href='mailto:cdl@pharm-rxchem.com'>cdl@pharm-rxchem.com</a>
              </span></p>
              <p><span className='thick-text'>Ann Smith - Operations Manager:<br />
                <a href='mailto:ams@pharm-rxchem.com'>ams@pharm-rxchem.com</a>
              </span></p>
            </div>
            <div className='contact-form'>
              <h2>Send Us a Message</h2>
              <form noValidate onSubmit={handleSubmit}>
                <input type='text' placeholder='Your Name' name='name' required />
                <input type='text' placeholder='Your Company' name='company' required />
                <input type='email' placeholder='Your Email' name='email' required />
                <div className="phone-input-wrapper">
                  <PhoneInput
                    placeholder="Your Phone Number"
                    value={phone}
                    onChange={setPhone}
                    defaultCountry="US"
                  />
                </div>
                <input type='text' placeholder='Subject' name='subject' required />
                <textarea placeholder='Your Message' name='message' required></textarea>

                <div id="recaptcha-container-contact"></div>

                <button type='submit'>Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showPopup && (
        <div className={`popup ${popupType}`}>
          {popupType === 'success' && <CiCircleCheck className="popup-icon" />}
          {popupType === 'error' && <CiCircleRemove className="popup-icon" />}
          {popupMessage}
        </div>
      )}

      <div className="faq-banner-section">
        <div className="faq-banner-header">
          <h2>Get Help Without the Wait</h2>
        </div>
        <div className="faq-banner-text">
          <p>Check out our FAQ page—your answer might be waiting for you there!</p>
        </div>
        <div className="faq-banner-button">
          <Link to="/faq" className="faq-banner-btn">Visit FAQ</Link>
        </div>
      </div>
    </div>
  );
}

export default ContactPage;
